import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  Home, Wrench, ShoppingBag, Package, Settings, Menu, X,
  ShoppingCart, User, LogOut, UserCircle
} from 'lucide-react';

const NavBar = ({ currentPage, setCurrentPage, mobileMenuOpen, setMobileMenuOpen }) => {
  const { currentUser, logout } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();

  const navigation = [
    { id: 'home', name: 'الرئيسية', icon: <Home className="w-5 h-5" /> },
    { id: 'maintenance', name: 'الصيانة المنزلية', icon: <Wrench className="w-5 h-5" /> },
    { id: 'accessories', name: 'الاكسسوارات', icon: <ShoppingBag className="w-5 h-5" /> },
    { id: 'parts', name: 'قطع الغيار', icon: <Package className="w-5 h-5" /> },
    { id: 'modifications', name: 'التعديلات', icon: <Settings className="w-5 h-5" /> }
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-black border-b border-brand-gray-600/30 z-50 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">

          {/* Logo */}
          <div
            className="flex items-center gap-3 md:gap-4 cursor-pointer group"
            onClick={() => setCurrentPage('home')}
          >
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-brand-orange-600/20 rounded-full blur-xl group-hover:bg-brand-orange-600/30 transition-all duration-300"></div>
              <img
                src="/3.png"
                alt="شعار الموقع"
                className="relative w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-white rounded-full shadow-2xl p-1.5 object-contain ring-2 ring-brand-orange-600/50 group-hover:ring-brand-orange-600 transition-all duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-white tracking-tight leading-none drop-shadow-2xl whitespace-nowrap">
                LUXURY CAR
              </h1>
              <p className="hidden sm:block text-[10px] md:text-xs text-brand-orange-500 font-medium tracking-wider">
                Premium Auto Services
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-2 bg-brand-gray-700 rounded-2xl p-1.5 border border-brand-gray-600">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`group relative flex items-center gap-2 px-3 xl:px-4 py-2.5 rounded-xl font-bold transition-all duration-300 ${currentPage === item.id
                    ? 'bg-brand-orange-600 text-white shadow-lg shadow-brand-orange-600/30 scale-105 led-glow'
                    : 'text-gray-300 hover:bg-brand-gray-600 hover:text-white hover:scale-105'
                  }`}
              >
                {item.icon}
                <span className="hidden xl:inline text-sm">{item.name}</span>
                {currentPage !== item.id && (
                  <div className="absolute inset-0 rounded-xl bg-brand-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-3">

            {/* Cart */}
            <button
              onClick={() => navigate('/cart')}
              className="hidden md:flex items-center justify-center bg-brand-gray-700 hover:bg-brand-gray-600 p-2.5 md:p-3 rounded-xl transition-all duration-300 hover:scale-105 relative group border border-brand-gray-600"
            >
              <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 text-white" />
              {getItemCount() > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-brand-orange-600 text-white text-xs w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center font-bold shadow-lg shadow-brand-orange-600/50 ring-2 ring-black">
                  {getItemCount()}
                </span>
              )}
            </button>

            {/* User Menu */}
            {currentUser ? (
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => navigate('/profile')}
                  className="bg-brand-gray-700 hover:bg-brand-gray-600 px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-105 border border-brand-gray-600"
                >
                  <UserCircle className="w-5 h-5 text-brand-orange-500" />
                  <span className="text-sm text-white font-semibold hidden lg:inline">
                    {currentUser.displayName || currentUser.email?.split('@')[0]}
                  </span>
                </button>
                <button
                  onClick={handleLogout}
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 p-2.5 md:p-3 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-red-500/30"
                  title="تسجيل الخروج"
                >
                  <LogOut className="w-5 h-5 text-white" />
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-brand-gray-700 hover:bg-brand-gray-600 px-4 lg:px-5 py-2.5 rounded-xl font-bold text-white transition-all duration-300 hover:scale-105 flex items-center gap-2 border border-brand-gray-600 text-sm"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden lg:inline">دخول</span>
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="bg-brand-orange-600 hover:bg-brand-orange-500 px-4 lg:px-6 py-2.5 rounded-xl font-bold text-white hover:scale-105 transition-all duration-300 shadow-lg text-sm"
                >
                  تسجيل
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden bg-brand-gray-700 hover:bg-brand-gray-600 p-2.5 md:p-3 rounded-xl transition-all duration-300 hover:scale-105 border border-brand-gray-600"
            >
              {mobileMenuOpen ?
                <X className="w-5 h-5 md:w-6 md:h-6 text-white" /> :
                <Menu className="w-5 h-5 md:w-6 md:h-6 text-white" />
              }
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-brand-gray-600/30 bg-brand-gray-800">
            <div className="py-4 space-y-2 px-2">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentPage(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-bold transition-all duration-300 ${currentPage === item.id
                      ? 'bg-brand-orange-600 text-white shadow-lg shadow-brand-orange-600/30'
                      : 'text-gray-300 hover:bg-brand-gray-600 hover:text-white active:scale-95'
                    }`}
                >
                  {item.icon}
                  <span className="text-sm md:text-base">{item.name}</span>
                </button>
              ))}
            </div>

            {/* Mobile Cart */}
            <div className="md:hidden px-2 pb-2">
              <button
                onClick={() => {
                  navigate('/cart');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-brand-gray-700 hover:bg-brand-gray-600 px-4 py-3.5 rounded-xl text-white flex items-center justify-between transition-all duration-300 active:scale-95 border border-brand-gray-600"
              >
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-bold text-sm">سلة التسوق</span>
                </div>
                {getItemCount() > 0 && (
                  <span className="bg-brand-orange-600 text-white text-xs px-2.5 py-1 rounded-full font-bold">
                    {getItemCount()}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Auth Buttons */}
            <div className="px-2 pb-4 space-y-2 border-t border-brand-gray-600/30 pt-4">
              {currentUser ? (
                <>
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-brand-gray-700 hover:bg-brand-gray-600 px-4 py-3.5 rounded-xl text-white flex items-center gap-3 transition-all duration-300 active:scale-95 border border-brand-gray-600"
                  >
                    <UserCircle className="w-5 h-5 text-brand-orange-500" />
                    <span className="font-bold text-sm">{currentUser.displayName || currentUser.email?.split('@')[0]}</span>
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 px-4 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all duration-300 active:scale-95 shadow-lg shadow-red-500/30"
                  >
                    <LogOut className="w-5 h-5" />
                    تسجيل الخروج
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-brand-gray-700 hover:bg-brand-gray-600 px-4 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all duration-300 active:scale-95 border border-brand-gray-600 text-sm"
                  >
                    <User className="w-5 h-5" />
                    تسجيل الدخول
                  </button>
                  <button
                    onClick={() => {
                      navigate('/register');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-brand-orange-600 hover:bg-brand-orange-500 px-4 py-3.5 rounded-xl font-bold text-white transition-all duration-300 active:scale-95 shadow-lg text-sm"
                  >
                    إنشاء حساب
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;